<template>
    <div class="col-sm-12 nopads">
        <keep-alive>
             <spreader-data-search @search="fetchSpreaderData"/>
        </keep-alive>
        <Transition v-if="results && results.length > 0" name="fade">
                <spreader-material-charts :results="results" class="mt-2 mb-2"/>
            </Transition>
            <spreader-data-list
                :results="results"
                @rowSelected="handleRowSelection"
                @showAll="showAllOnMap"
                @exportToExcel="exportToExcel"
                @removeItem="onRemoveItem"
            />
            <!-- Map modal for showing points -->
            <Transition name="fade">
                <spreader-data-map
                    v-if="mapVisible"
                    :spreader-data="details"
                    :spreader-all-data="multipleDetails"
                    @close="mapVisible = false"
                />
            </Transition>
            <!-- Harja submission modal -->
            <base-modal
                v-if="harjaSenderVisible"
                size="xl"
                :hide-footer="true"
                :title="$t('spreader_data.send_to_harja_title')"
            >
                <spreader-data-harja-sender
                    :user="this.user"
                    :item="spreaderDataToharjaItem"
                    :preSelectedOrder="prefillOrder"
                    @close="closeHarjaSender"
                    @triggerListUpdate="triggerListUpdate"
                    @closeAndUpdate="closeAndUpdate"
                />
            </base-modal>
            <!-- Harja removal modal -->
            <base-modal
                v-if="harjaRemoverVisible"
                :hide-footer="true"
                :hide-header-close="true"
                :title="$t('spreader_data.remove_external')"
            >
                <div class="col-12 mb-4">
                    <span>{{ $t('spreader_data.remove_text')}}</span>
                </div>
                <div class="col-12 d-flex justify-content-end">
                    <button
                        class="btn btn-secondary form-button"
                        @click="closeRemoveModal">
                        {{ $t('common.cancel')}}
                    </button>
                    <button
                        class="btn btn-danger form-button"
                        @click="submitExternalRemoval">
                        {{ $t('spreader_data.remove_data')}}
                    </button>
                </div>
            </base-modal>
           <div
                v-if="loading"
                id="loader"
                class="spinner"
            />
        </div>
</template>


<script>
import SpreaderDataSearch from "@/components/materials/spreaderdata/SpreaderDataSearch";
import SpreaderDataList from "@/components/materials/spreaderdata/SpreaderDataList";
import SpreaderDataHarjaSender from "@/components/materials/spreaderdata/SpreaderDataHarjaSender";
import {restApi} from "@/components/mixins/RestApiMixin";
import SpreaderMaterialCharts from "@/components/materials/spreaderdata/SpreaderMaterialCharts";
import SpreaderDataMap from "@/components/materials/spreaderdata/SpreaderDataMap";
import {spreaderDataMixin} from "@/components/mixins/SpreaderDataMixin";
import {mapHelper} from "@/components/mixins/MapMixin";
import {timeUtils} from "@/components/mixins/TimeUtils";
import {downloadHelper} from "@/components/mixins/DownloadMixin";
import Navbar from "@/components/utils/Navbar";
import BaseModal from "@/components/BaseModal";
import {inject, provide} from "vue";

export default {
    name: 'SpreaderData',
    components: {
        BaseModal,
        Navbar,
        SpreaderDataMap,
        SpreaderMaterialCharts,
        SpreaderDataList,
        SpreaderDataSearch,
        SpreaderDataHarjaSender
    },
    mixins: [restApi, spreaderDataMixin, mapHelper, timeUtils, downloadHelper],
    props: {
        user: {
            type: Object,
            default: null
        }
    },
    setup(props) {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        if (props.user) {
            provide('user', props.user);
        }
        return { showAlert };
    },
    data: function () {
        return {
            results: [],
            details: null,
            multipleDetails: [],
            markerSelections: [],
            loading: false,
            mapVisible: false,
            harjaSenderVisible: false,
            harjaRemoverVisible: false,
            spreaderDataToharjaItem: {},
            lastSearchedParams: null,
            prefillOrder: null
        }
    },
    mounted: function () {
        this.lastSearchedParams = {}
    },
    methods: {
        fetchSpreaderData: function (params) {
            this.loading = true
            this.lastSearchedParams = params
            this.restFetchParams(this.spreaderDataUrl, this.lastSearchedParams, this.handleResponse)
        },
        handleResponse: function (response) {
            this.loading = false
            if (response && response.data) {
                this.results = response.data
            }
        },
        handleRowSelection: function (item, openHarjaSender = false) {
            if(openHarjaSender) {
                this.spreaderDataToharjaItem = item
                this.harjaSenderVisible = true
            } else {
                this.showDataOnMap(item)
            }
        },

        onRemoveItem(item) {
            this.spreaderDataToharjaItem = item
            this.harjaRemoverVisible = true
        },

        submitExternalRemoval() {
            if (this.spreaderDataToharjaItem) {
                this.loading = true
                this.restAdd(this.spreaderDataExtRemovalUrl, {items: [this.spreaderDataToharjaItem.id]},
                    this.removalSuccess, this.removalFailed)
            }
        },

        removalSuccess() {
            this.loading = false
            this.harjaRemoverVisible = false
            this.fetchSpreaderData(this.lastSearchedParams)
        },

        removalFailed() {
            this.loading = false
           this.showAlert(this.$t('spreader_data.removal_error'))
        },

        closeRemoveModal() {
            this.harjaRemoverVisible = false
            this.spreaderDataToharjaItem = {}
        },

        showDataOnMap: function (item) {
            this.loading = true
            if (this.lastSearchedParams && this.lastSearchedParams.roadNum) {
                const params = {
                    roadNum: this.lastSearchedParams.roadNum,
                    secFrom: this.lastSearchedParams.secFrom,
                    distFrom: this.lastSearchedParams.distFrom,
                    secTo: this.lastSearchedParams.secTo,
                    distTo: this.lastSearchedParams.distTo
                }
                this.restFetchParams(this.spreaderDataUrl+"/"+item.id, params, this.handleDetails)
            } else {
                this.restFetch(this.spreaderDataUrl+"/"+item.id, this.handleDetails)
            }
        },

        handleDetails(response) {
            this.loading = false
            if (response && response.data) {
                this.details = response.data
                this.multipleDetails = null
                this.mapVisible = true
            }
        },
        handleMultipleDetails(response) {
            this.loading = false
            if (response && response.data) {
                this.details = null
                this.multipleDetails = response.data
                this.mapVisible = true
            } else if(response && response.status === 500) {
                this.showAlert(this.$t('trip_list.err_too_much'))
            }
        },
        closeAndUpdate(order) {
            this.prefillOrder = order
            this.closeHarjaSender()
            this.fetchSpreaderData(this.lastSearchedParams)
        },
        triggerListUpdate() {
            this.fetchSpreaderData(this.lastSearchedParams)
        },
        closeHarjaSender: function () {
            this.spreaderDataToharjaItem = {}
            this.harjaSenderVisible = false
        },

        showAllOnMap() {
            if(this.results && this.results.length > 0) {
                let params = this.lastSearchedParams
                params.geometry = 1
                this.loading = true
                this.restFetchParams(this.spreaderDataUrl, params, this.handleMultipleDetails)
            }
        },

        exportToExcel() {
            this.loading = true
            this.restGetDownloadFile(this.spreaderDataXlsxUrl, {
                ...this.lastSearchedParams,
                ...this.getCommonReportParams(this.lastSearchedParams)
            }, this.excelDownloadSuccess, this.fileDownloadFail)
        },
        excelDownloadSuccess: function (response) {
            this.loading = false
            this.downloadFile(response, this.$t('spreader_data.report_file_name'))
        },
        fileDownloadFail: function () {
            this.loading = false
            this.showAlert(this.$t('common.report_generation_failed'))
        },
    }
}
</script>
<style scoped>
    .nooverflow {
        overflow: hidden;
    }
    .harja-sender-modal {
        /** Show Bus event aler banner top of the modal */
        z-index: 4999 !important;
    }
</style>
